import React, { useMemo, useState } from 'react';
import useTable from 'shared/hooks/customHooks/useTable';
import PayerAccountsHeader from './PayerAccountsHeader.jsx';
import {
  getIsAccountSelected,
  getLinkedAccountIsSelectionEnabled,
  getLinkedAccountsIsSelectionHidden,
  getPayerIsSelectionEnabled,
} from './dataAccessHelperFunctions';
import { ACCOUNT_TYPES } from 'users/containers/Organization/consts.js';
import PayerAccountsTable from 'users/containers/Organization/components/LinkedAccounts/PayerAccountsTable';

import styles from './AccountsComponent.module.scss';

function getAllEnabledPayers(allAccountsData, isCreateEditMode, entity) {
  return allAccountsData?.accountsData
    ? Object.values(allAccountsData?.accountsData)
        .flatMap((account) => account)
        .map((account) => account.payerAccount)
        .filter((payer) => getPayerIsSelectionEnabled(payer, isCreateEditMode, entity))
    : [];
}

function getAllEnabledLinkedAccounts(allAccountsData, modifiedSelectionById, entity, isCreateEditMode) {
  return allAccountsData?.accountsData
    ? Object.values(allAccountsData?.accountsData)
        .flatMap((account) => account)
        .filter(
          (account) =>
            !getPayerIsSelectionEnabled(account.payerAccount, isCreateEditMode, entity) &&
            !getIsAccountSelected(account.payerAccount, modifiedSelectionById),
        )
        .map((account) => ({ linkedAccounts: account.linkedAccounts, payerAccount: account.payerAccount }))
        .map((pa) => ({
          linkedAccounts: pa.linkedAccounts.filter(
            (linkedAccount) =>
              getLinkedAccountIsSelectionEnabled(linkedAccount, entity) &&
              !getLinkedAccountsIsSelectionHidden(pa.payerAccount, modifiedSelectionById?.[pa.payerAccount.id], entity),
          ),
          payerAccount: pa.payerAccount,
        }))
        .filter((pa) => pa.linkedAccounts?.length)
    : [];
}

const AccountsComponent = ({
  additionalData,
  allAccountsData,
  entity,
  isCreateEditMode,
  isViewOnly,
  linkedAccountsColumns,
  modifiedSelectionById,
  payerAccountsColumns,
  onFullAccessSelectChanged,
  onSelectionChange,
  onSelectAllChanged,
}) => {
  const [search, setSearch] = useState();
  const [payerAccountsExpandedRows, setPayerAccountsExpandedRows] = useState([]);

  const { NewTableWrapper } = useTable();

  const filteredAccountsData = useMemo(() => {
    if (!allAccountsData?.accountsData) {
      return null;
    }
    if (!search) {
      return allAccountsData;
    }
    const { accountsData } = allAccountsData;

    function searchArray(arr) {
      return arr
        .map((item) => {
          const newItem = { ...item };
          if (Array.isArray(newItem.linkedAccounts)) {
            newItem.linkedAccounts = searchArray(newItem.linkedAccounts);
          }
          if (
            newItem.payerAccount?.name?.toLowerCase().includes(search?.toLowerCase()) || // Search in names of Payer accounts level
            newItem.payerAccount?.id?.toLowerCase().includes(search?.toLowerCase()) || // Search in ids of Payer accounts level
            newItem.name?.toLowerCase().includes(search?.toLowerCase()) || // Search in names of Linked accounts level
            newItem.id?.toLowerCase().includes(search?.toLowerCase()) || // Search in ids of Linked accounts level
            newItem.linkedAccounts?.length > 0
          ) {
            return newItem;
          }
          return null;
        })
        .filter((item) => item !== null);
    }

    const filteredData = searchArray(accountsData);

    return { ...allAccountsData, accountsData: filteredData };
  }, [search, allAccountsData]);

  const handlePayerAccountsExpandedRowsChange = (expandedRowsIds) => {
    setPayerAccountsExpandedRows(expandedRowsIds);
  };

  const handleSelectAll = (isSelected) => {
    const allSelection = { [ACCOUNT_TYPES.PAYER_ACCOUNT]: [], [ACCOUNT_TYPES.LINKED_ACCOUNT]: [] };
    if (isSelected === true) {
      const payerIds = isSelected
        ? getAllEnabledPayers(allAccountsData, isCreateEditMode, entity).map((payer) => payer.id)
        : [];
      allSelection[ACCOUNT_TYPES.PAYER_ACCOUNT] = payerIds;

      if (modifiedSelectionById) {
        const linkedAccountPerPayer = isSelected
          ? getAllEnabledLinkedAccounts(allAccountsData, modifiedSelectionById, entity, isCreateEditMode)
          : [];
        linkedAccountPerPayer.forEach((pa) => allSelection[ACCOUNT_TYPES.LINKED_ACCOUNT].push(pa));
      }
    } else {
      const allAccounts = Object.values(allAccountsData?.accountsData).flatMap((account) => account);
      const selectedLinkedAccounts = allAccounts.flatMap((account) => {
        return {
          payerAccount: account.payerAccount,
          linkedAccounts: [],
        };
      });

      allSelection[ACCOUNT_TYPES.PAYER_ACCOUNT] = [];
      allSelection[ACCOUNT_TYPES.LINKED_ACCOUNT] = selectedLinkedAccounts;
    }
    onSelectAllChanged(allSelection);
  };

  const isAllSelected = useMemo(() => {
    // If payer is not selectable, it should be considered as selected, even if it is not
    const payerAccounts = getAllEnabledPayers(allAccountsData, isCreateEditMode, entity);
    let linkedAccounts = [];
    if (modifiedSelectionById) {
      linkedAccounts = getAllEnabledLinkedAccounts(allAccountsData, modifiedSelectionById, entity).flatMap(
        (pa) => pa.linkedAccounts,
      );
    }
    const areAllPayerAccountsSelected =
      payerAccounts?.length === 0 || payerAccounts.every((payer) => getIsAccountSelected(payer, modifiedSelectionById));
    const areAllLinkedAccountsSelected =
      linkedAccounts?.length === 0 ||
      linkedAccounts.every((linkedAccount) => getIsAccountSelected(linkedAccount, modifiedSelectionById));

    return (
      areAllPayerAccountsSelected &&
      areAllLinkedAccountsSelected &&
      !(payerAccounts?.length === 0 && linkedAccounts?.length === 0)
    );
  }, [allAccountsData, isCreateEditMode, entity, modifiedSelectionById]);

  return (
    <div className={styles.payerAccountsContainer}>
      <NewTableWrapper>
        <PayerAccountsHeader
          allAccountsData={filteredAccountsData}
          entity={entity}
          isCreateEditMode={isCreateEditMode}
          isSelectAll={isAllSelected}
          isViewOnly={isViewOnly}
          modifiedSelectionById={modifiedSelectionById}
          onSelectAll={handleSelectAll}
          onFullAccessSelectChanged={onFullAccessSelectChanged}
          search={search}
          setSearch={setSearch}
        />
        <PayerAccountsTable
          accountsData={filteredAccountsData.accountsData}
          additionalData={additionalData}
          entity={entity}
          expandedRows={payerAccountsExpandedRows}
          isCreateEditMode={isCreateEditMode}
          isViewOnly={isViewOnly || !!allAccountsData?.dataAccessWithFullAccess}
          isFullAccess={filteredAccountsData?.fullAccessData?.isFullAccess}
          linkedAccountsColumns={linkedAccountsColumns}
          modifiedSelectionById={modifiedSelectionById}
          onExpandedRowsChange={handlePayerAccountsExpandedRowsChange}
          onSelectionChange={onSelectionChange}
          payerAccountsColumns={payerAccountsColumns}
        />
      </NewTableWrapper>
    </div>
  );
};


export default AccountsComponent;
